import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import TemplateStylePreview, {
  DESIGN_STYLE_OPTIONS,
  LAYOUT_STYLE_OPTIONS,
  getTemplateStyle
} from './TemplateStylePreview.jsx';
import './TemplateLibrary.css';

const LAYOUT_FAMILY_OPTIONS = [
  { id: 'All', label: 'All Families' },
  { id: 'narrative', label: 'Narrative' },
  { id: 'grid', label: 'Grid' },
  { id: 'text-only', label: 'Text-Only' },
  { id: 'image-led', label: 'Image-Led' }
];

const TEMPLATE_LAYOUT_FAMILY_MAP = {
  'modern-clean': 'narrative',
  corporate: 'narrative',
  newsletter: 'grid',
  promotional: 'image-led',
  'event-invite': 'image-led',
  'product-update': 'narrative',
  announcement: 'text-only',
  ecommerce: 'grid',
  'zig-zag-story': 'narrative',
  'three-card-grid': 'grid',
  'minimal-text-only': 'text-only',
  'image-quote-focus': 'image-led',
  'cinematic-hero': 'image-led',
  'glassmorphism-showcase': 'grid',
  'parallax-postcard': 'image-led',
  'event-timeline-spotlight': 'image-led',
  'event-vip-launch': 'image-led',
  'event-speaker-showcase': 'image-led',
  'event-countdown-reminder': 'image-led',
  'event-workshop-track': 'image-led'
};

const getLayoutFamily = (template, preview) => {
  if (!template?.id) return 'narrative';
  if (TEMPLATE_LAYOUT_FAMILY_MAP[template.id]) return TEMPLATE_LAYOUT_FAMILY_MAP[template.id];

  if (preview?.layoutStyle === 'card-sections') return 'grid';
  if (preview?.layoutStyle === 'hero-cta') return 'image-led';
  if (preview?.layoutStyle === 'single-column-story') return 'text-only';
  return 'narrative';
};

const TemplateLibrary = ({ onTemplateSelected, onClose, onUseStyleInAI, isLoading = false }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [designFilter, setDesignFilter] = useState('All');
  const [layoutFilter, setLayoutFilter] = useState('All');
  const [layoutFamilyFilter, setLayoutFamilyFilter] = useState('All');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/templates/library');

      if (response.data.success) {
        setTemplates(response.data.templates);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategories = () => {
    const categories = new Set(templates.map((template) => template.category));
    return ['All', ...Array.from(categories)];
  };

  const filteredTemplates = templates.filter((template) => {
    const preview = getTemplateStyle(template);
    const layoutFamily = getLayoutFamily(template, preview);
    const categoryMatches = categoryFilter === 'All' || template.category === categoryFilter;
    const designMatches = designFilter === 'All' || preview.designStyle === designFilter;
    const layoutMatches = layoutFilter === 'All' || preview.layoutStyle === layoutFilter;
    const layoutFamilyMatches = layoutFamilyFilter === 'All' || layoutFamily === layoutFamilyFilter;
    return categoryMatches && designMatches && layoutMatches && layoutFamilyMatches;
  });

  const handleApplyTemplate = () => {
    if (selectedTemplate && onTemplateSelected) {
      onTemplateSelected(selectedTemplate);
    }
  };

  const buildCardPreviewDocument = (html) => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body { margin: 0; padding: 0; background: #f2f4f8; }
      .preview-root { width: 600px; margin: 0 auto; }
      img { max-width: 100%; height: auto; }
    </style>
  </head>
  <body>
    <div class="preview-root">${html || ''}</div>
  </body>
</html>`;

  if (loading) {
    return (
      <div className="template-library">
        <div className="library-header">
          <div>
            <h2>Email Template Library</h2>
            <p>Curated, email-safe starting points.</p>
          </div>
          <button type="button" className="close-button" onClick={onClose} title="Close">✕</button>
        </div>
        <div className="loading-state">
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="template-library">
        <div className="library-header">
          <div>
            <h2>Email Template Library</h2>
            <p>Curated, email-safe starting points.</p>
          </div>
          <button type="button" className="close-button" onClick={onClose} title="Close">✕</button>
        </div>
        <div className="error-state">
          <p>⚠️ {error}</p>
          <button type="button" onClick={loadTemplates} className="retry-button">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="template-library">
      <div className="library-header">
        <div>
          <h2>Email Template Library</h2>
          <p>Preview actual template output before you customize anything.</p>
        </div>
        <button type="button" className="close-button" onClick={onClose} title="Close">✕</button>
      </div>

      <div className="library-filter-sections">
        <div className="filter-group">
          <label className="filter-group-label" htmlFor="template-filter-category">Category</label>
          <select
            id="template-filter-category"
            className="filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {getCategories().map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-group-label" htmlFor="template-filter-design">Design Style</label>
          <select
            id="template-filter-design"
            className="filter-select"
            value={designFilter}
            onChange={(e) => setDesignFilter(e.target.value)}
          >
            {DESIGN_STYLE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-group-label" htmlFor="template-filter-layout-style">Layout Style</label>
          <select
            id="template-filter-layout-style"
            className="filter-select"
            value={layoutFilter}
            onChange={(e) => setLayoutFilter(e.target.value)}
          >
            {LAYOUT_STYLE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-group-label" htmlFor="template-filter-layout-family">Layout Family</label>
          <select
            id="template-filter-layout-family"
            className="filter-select"
            value={layoutFamilyFilter}
            onChange={(e) => setLayoutFamilyFilter(e.target.value)}
          >
            {LAYOUT_FAMILY_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="templates-grid">
        {filteredTemplates.length > 0 ? (
          filteredTemplates.map((template) => {
            const preview = getTemplateStyle(template);
            return (
              <button
                key={template.id}
                type="button"
                className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="template-card-topline">
                  <span className="category-badge">{template.category}</span>
                  <span className="style-badge">{preview.layoutStyle.replace(/-/g, ' ')}</span>
                </div>

                {template.previewHtml ? (
                  <div className="template-live-preview">
                    <div className="template-live-preview-viewport">
                      <iframe
                        className="template-live-preview-frame"
                        title={`${template.name} preview`}
                        srcDoc={buildCardPreviewDocument(template.previewHtml)}
                        loading="lazy"
                        sandbox=""
                      />
                    </div>
                  </div>
                ) : (
                  <TemplateStylePreview template={template} />
                )}

                <div className="template-info">
                  <h3>{template.name}</h3>
                  <p className="description">{template.description}</p>
                  <div className="template-meta-row">
                    <span>{DESIGN_STYLE_OPTIONS.find((option) => option.id === preview.designStyle)?.label || 'Modern Corporate'}</span>
                    <span>{template.customizableFields?.length || 0} editable fields</span>
                  </div>
                </div>

                {selectedTemplate?.id === template.id && (
                  <div className="selection-checkmark">✓</div>
                )}
              </button>
            );
          })
        ) : (
          <div className="no-templates">
            <p>No templates match those filters.</p>
          </div>
        )}
      </div>

      {selectedTemplate ? (
        <div className="selection-info">
          <div className="selected-template-preview">
            <h4>{selectedTemplate.name}</h4>
            <p>{selectedTemplate.description}</p>
            <p className="fields-info">
              {selectedTemplate.customizableFields?.length || 0} fields are ready to customize after selection.
            </p>
          </div>
          <button
            type="button"
            className="use-style-button"
            onClick={() => onUseStyleInAI && onUseStyleInAI(selectedTemplate)}
            disabled={isLoading}
          >
            Use Style in AI Assist
          </button>
          <button
            type="button"
            className="apply-template-button"
            onClick={handleApplyTemplate}
            disabled={isLoading}
          >
            {isLoading ? 'Applying...' : 'Next: Customize →'}
          </button>
        </div>
      ) : (
        <div className="no-selection">
          <p>Select a style system and composition to continue.</p>
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;
